import { useMemo } from "react";
import {
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  YAxis,
} from "recharts";

export const ReportIndicatorChart = ({
  referenceLineCoordinates,
  unitSymbol = "°",
  values,
}) => {
  // Normalize + precompute everything once
  const { areaMax, areaMin, data, yDomain } = useMemo(() => {
    // data for Recharts
    const _data = (values ?? []).map((v, i) => ({
      name: String(i),
      pv: Number(v),
    }));

    const a = Number(referenceLineCoordinates?.[0] ?? 0);
    const b = Number(referenceLineCoordinates?.[1] ?? 0);
    const [minRaw, maxRaw] = a <= b ? [a, b] : [b, a];

    // match legacy rounding
    const areaMin = Number(minRaw.toFixed(1));
    const areaMax = Number(maxRaw.toFixed(1));
    const padding = Number(((areaMax - areaMin) * 0.5).toFixed(1));
    const yDomain = [
      Number((areaMin - padding).toFixed(1)),
      Number((areaMax + padding).toFixed(1)),
    ];

    return { areaMax, areaMin, data: _data, yDomain };
  }, [referenceLineCoordinates, values]);

  // Fallback to avoid recharts warnings when no values
  const hasData =
    data.length > 0 && Number.isFinite(areaMin) && Number.isFinite(areaMax);

  // dot color depends on whether point is inside reference band
  const renderCustomDot = (props) => {
    const { cx, cy, value } = props;
    // default (orange), inside band -> green
    const fill = value > areaMin && value < areaMax ? "#12B76A" : "#FDB022";
    return <circle cx={cx} cy={cy} fill={fill} r={3} stroke="none" />;
  };

  return (
    <div className="font-manrope">
      <ResponsiveContainer height={70} width={130}>
        {hasData ? (
          <LineChart
            data={data}
            margin={{ bottom: 5, left: 5, right: 5, top: 5 }}
          >
            <YAxis
              axisLine={false}
              domain={yDomain}
              tick={{ fontFamily: "monospace", fontSize: 8 }}
              tickLine={false}
              unit={unitSymbol}
              width={30}
            />

            {/* Reference band */}
            <ReferenceArea
              fill="#12B76A"
              fillOpacity={0.1}
              y1={areaMin}
              y2={areaMax}
            />
            <ReferenceLine stroke="#12B76A" strokeWidth={1} y={areaMin} />
            <ReferenceLine stroke="#12B76A" strokeWidth={1} y={areaMax} />

            {/* Player series */}
            <Line
              dataKey="pv"
              dot={renderCustomDot}
              isAnimationActive={false} // safer for html2canvas
              stroke="#98A2B3"
              strokeDasharray="2 2"
              strokeWidth={1}
              type="linear"
            />
          </LineChart>
        ) : (
          // keep a valid SVG to avoid layout jumps; blank chart
          <LineChart data={[{ name: "0", pv: 0 }]}>
            <YAxis hide />
          </LineChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};
